/**
 * Job cost rows table — hover pencil/trash (#344), cash-forecast note for unpaid+due.
 */
import { Pencil, Trash2 } from 'lucide-react'
import { formatMoney } from '../../../../lib/formatters'
import type { JobCostRow } from '../../../../api/costsMargin'

function fmtDue(iso: string | null): string {
  if (!iso) return ''
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return iso
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

export default function CostsTable({
  costs,
  onEdit,
  onDelete,
}: {
  costs: JobCostRow[]
  onEdit: (row: JobCostRow) => void
  onDelete: (row: JobCostRow) => void
}) {
  if (costs.length === 0) return null
  return (
    <div data-testid="cm-costs-table">
      <div className="cm-section-title" style={{ borderTop: '1px solid var(--line)' }}>
        <h3>Other costs</h3>
        <span>{costs.length} cost{costs.length === 1 ? '' : 's'} entered</span>
      </div>
      <div className="cm-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th>Category / vendor</th>
              <th>Status</th>
              <th>Amount</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {costs.map((row) => {
              const paid = !!row.paid_at
              const unpaidDue = !paid && !!row.due_date
              const dateNeeded = !paid && !row.due_date
              return (
                <tr key={row.id} data-testid={`cm-cost-row-${row.id}`}>
                  <td><strong>{row.description}</strong></td>
                  <td>
                    {row.category}
                    {row.vendor_name ? ` · ${row.vendor_name}` : ''}
                  </td>
                  <td>
                    <span className={paid ? 'cm-status-paid' : 'cm-status-due'}>
                      {paid ? 'Paid' : `Unpaid${row.due_date ? ` · ${fmtDue(row.due_date)}` : ''}`}
                    </span>
                    {unpaidDue ? (
                      <span className="cm-cash-flow">Flows into cash forecast</span>
                    ) : null}
                    {dateNeeded ? (
                      <span className="cm-date-needed" data-testid={`cm-date-needed-${row.id}`}>date needed</span>
                    ) : null}
                  </td>
                  <td className="money">{formatMoney(row.amount_cents / 100)}</td>
                  <td className="hub-row-actions-cell">
                    <span className="hub-row-actions">
                      <button
                        type="button"
                        className="hub-row-action-btn"
                        aria-label={`Edit ${row.description}`}
                        onClick={() => onEdit(row)}
                      >
                        <Pencil size={16} strokeWidth={2} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="hub-row-action-btn hub-row-action-btn--delete"
                        aria-label={`Delete ${row.description}`}
                        onClick={() => onDelete(row)}
                      >
                        <Trash2 size={16} strokeWidth={2} aria-hidden />
                      </button>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
